import resolveLastViewedDate from './resolveLastViewedDate';

export interface PublishableRouteLike {
  projectId?: string | number | null;
  isDispatched?: boolean;
  unpublishedChanges?: boolean;
  /** Finished routes are not publishable even while `unpublishedChanges` is still set. */
  hasPublishablePendingChanges?: boolean;
  resolvedActiveRouteSolution?: { stopsCount?: number | null } | null;
}

interface WorkspaceLike {
  id?: string | number | null;
  startDate?: string | null;
  source?: string | null;
}

/**
 * A route counts as publishable when it has stops but has never been
 * dispatched, or when it was dispatched and has been edited since.
 * Shared by the Publish call-to-action badges (nav + ViewMenu) and
 * the `publishableRoutes` store view.
 */
export const isPublishableRoute = (route: PublishableRouteLike): boolean => {
  // Route models expose hasPublishablePendingChanges (false once finished).
  // Plain fixtures only set unpublishedChanges.
  const pendingChanges = route.hasPublishablePendingChanges ?? route.unpublishedChanges;
  return (
    (!route.isDispatched && (route.resolvedActiveRouteSolution?.stopsCount || 0) > 0)
    || (!!route.isDispatched && !!pendingChanges)
  );
};

/**
 * Count for the nav's publish call-to-action badge. Resolves the day the
 * routes page would land on (URL date when present, else the persisted
 * last-viewed date) so the badge survives dateless pages (drivers,
 * customers) and non-workspace pages (insights, settings), where it
 * reflects the most recently viewed workspace's still-cached routes.
 */
const getUnpublishedRoutesCount = (
  sortedRoutesByDay: (date?: string) => PublishableRouteLike[],
  selectedDate: string | undefined,
  displayedWorkspace?: WorkspaceLike,
): number => {
  const date = selectedDate ?? resolveLastViewedDate(displayedWorkspace);
  const workspaceId = displayedWorkspace?.id;
  return sortedRoutesByDay(date)
    // routesRange only scopes reads while a workspace is selected; on
    // non-workspace pages it returns every cached route, so re-scope to the
    // workspace this badge represents. Routes without a projectId
    // (optimistic local ones) stay counted, mirroring routesRange's guard.
    .filter((route) => workspaceId == null || route.projectId == null || String(route.projectId) === String(workspaceId))
    .filter(isPublishableRoute).length;
};

export default getUnpublishedRoutesCount;
